import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Scale, Landmark, Gavel, BookOpen, AlertTriangle, CheckCircle2 } from 'lucide-react';

const SectionCard = ({ icon: Icon, title, badge, children, testId }) => (
  <section data-testid={testId} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 sm:p-7 space-y-4">
    <div className="flex items-start sm:items-center gap-3 flex-wrap">
      <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-red-700/10 text-red-700 shrink-0">
        <Icon className="w-5 h-5" />
      </span>
      <h2 className="text-base sm:text-lg font-semibold text-slate-900 tracking-tight">{title}</h2>
      {badge && (
        <span className="text-[11px] sm:text-xs font-semibold px-2.5 py-1 rounded-full bg-red-700 text-white">{badge}</span>
      )}
    </div>
    {children}
  </section>
);

const Item = ({ title, children }) => (
  <div className="flex gap-3">
    <div>
      <p className="text-sm font-semibold text-slate-900">{title}</p>
      <p className="text-sm text-slate-600 mt-0.5">{children}</p>
    </div>
  </div>
);

export default function LawChangesGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#f7f2e7]" data-testid="law-changes-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button
            data-testid="law-guide-back-btn"
            onClick={() => navigate('/dashboard')}
            className="flex items-center justify-center w-9 h-9 rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
            title="Επιστροφή"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-base sm:text-xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-red-700" />
              Οδηγός Νομοθετικών Αλλαγών
            </h1>
            <p className="hidden sm:block text-xs text-slate-500">Ν. 5221/2025 (ΚΠολΔ) · Ν. 5119/2024 (ΣτΕ) · Ν. 5090/2024 (ΚΠΔ) — και πώς τις χειρίζεται το COURTPLAN</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6">

        <SectionCard icon={Scale} title="Ν. 5221/2025 — Μεταρρύθμιση ΚΠολΔ" badge="Ισχύς 1.1.2026" testId="section-n5221">
          <div className="space-y-3">
            <Item title="Καταχρηστική προθεσμία έφεσης & αναίρεσης (518§2, 564§3)">
              Μειώνεται σε <b>1 έτος</b> από τη δημοσίευση (πριν: 2 έτη έφεση / 3 έτη αναίρεση). Ισχύει για αποφάσεις που δημοσιεύονται από 1.1.2026.
            </Item>
            <Item title="Έφεση — νέος τρόπος άσκησης (495, 498, 520, 523, 524)">
              Κατάθεση απευθείας στο Εφετείο με ορισμό δικασίμου εντός 5 μηνών. Επίδοση έφεσης εντός <b>30 ημερών</b> από την κατάθεση. Πρόσθετοι λόγοι & αντέφεση: <b>40 ημέρες από τη λήξη της προθεσμίας επίδοσης</b> (σύνολο 70 από κατάθεση) και επίδοσή τους εντός 30 ημερών από την άσκηση. Προσθήκη-αντίκρουση: έως τη 12:00 της <b>5ης εργάσιμης</b> μετά τη συζήτηση (πριν: 3ης).
            </Item>
            <Item title="Τακτική διαδικασία — Διάταξη προδικαστικού ελέγχου (237)">
              Οι προθεσμίες 30 (επίδοση) / 90 (προτάσεις) / 15 (προσθήκη) διατηρούνται. Νέο στάδιο: Διάταξη προδικαστικού ελέγχου — αντίλεξη του ενάγοντος εντός <b>5 ημερών</b> από την έκδοση, με παράβολο 200€. Η διαμεσολάβηση <b>δεν αναστέλλει</b> πλέον τις προθεσμίες.
            </Item>
            <Item title="Αναγκαστική εκτέλεση (933, 934, 954)">
              Ανακοπή 954§4 (διόρθωση κατασχετήριας έκθεσης): έως <b>30 εργάσιμες</b> ημέρες πριν τον πλειστηριασμό (πριν: 15). Περιορισμός λόγων ανακοπής ανά στάδιο (933§4). Υποχρεωτικός επαναπροσδιορισμός εκκρεμών ανακοπών με δικάσιμο μετά την 1.1.2026 μέσω ηλεκτρονικής πλατφόρμας, επί ποινή απαραδέκτου.
            </Item>
            <Item title="Διαταγή πληρωμής (630Α, 632, 633)">
              Η ΔΠ επιδίδεται εντός <b>2 μηνών</b> από την έκδοση, άλλως παύει να ισχύει. Το δικαίωμα ανακοπής εκτείνεται από την 1η επίδοση έως την πάροδο της προθεσμίας από τη 2η επίδοση (15 / 10 εργάσιμες αντίστοιχα).
            </Item>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mt-4" data-testid="transitional-criteria-table">
            <p className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              Μεταβατικά κριτήρια — ποιο καθεστώς εφαρμόζεται;
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3 font-medium">Κατηγορία</th>
                    <th className="py-2 font-medium">Κριτήριο για νέο δίκαιο</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700">
                  <tr className="border-b border-slate-100"><td className="py-2 pr-3">Αγωγές & κλήσεις</td><td className="py-2"><b>Κατάθεση</b> δικογράφου από 1.1.2026</td></tr>
                  <tr className="border-b border-slate-100"><td className="py-2 pr-3">Ένδικα μέσα & ειδικές διαδικασίες</td><td className="py-2"><b>Κατάθεση</b> του ενδίκου μέσου από 1.1.2026</td></tr>
                  <tr className="border-b border-slate-100"><td className="py-2 pr-3">Καταχρηστική έφεσης/αναίρεσης</td><td className="py-2"><b>Δημοσίευση</b> απόφασης από 1.1.2026</td></tr>
                  <tr><td className="py-2 pr-3">Αναγκαστική εκτέλεση</td><td className="py-2"><b>Επίδοση επιταγής</b> προς εκτέλεση από 1.1.2026</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </SectionCard>

        <SectionCard icon={Landmark} title="Ν. 5119/2024 — Δίκη στο Συμβούλιο της Επικρατείας" badge="Από 16.9.2024" testId="section-n5119">
          <div className="space-y-3">
            <Item title="Επίδοση εισαγωγικού δικογράφου — ΚΡΙΣΙΜΗ προθεσμία (άρθ. 21)">
              Ο διάδικος επιδίδει ο ίδιος το εισαγωγικό δικόγραφο εντός <b>2 μηνών</b> από την κατάθεση — άλλως το δικόγραφο τίθεται <b>στο αρχείο ως μη ασκηθέν</b>.
            </Item>
            <Item title="Υπόμνημα (άρθ. 25§2)">
              Κατατίθεται έως <b>15 πλήρεις ημέρες</b> πριν τη συζήτηση (πριν: 6).
            </Item>
            <Item title="Πρόσθετοι λόγοι (άρθ. 25§1)">
              Εντός <b>20 ημερών από την πάροδο τριμήνου</b> από την επίδοση του ενδίκου μέσου (ή την αποστολή του φακέλου) — σε κάθε περίπτωση τουλάχιστον 20 πλήρεις ημέρες πριν τη δικάσιμο.
            </Item>
            <Item title="Παρέμβαση (άρθ. 49)">
              Εντός <b>3 μηνών από την επίδοση</b> της αίτησης στον παρεμβαίνοντα — άλλως έως 15 πλήρεις ημέρες πριν τη δικάσιμο.
            </Item>
          </div>
        </SectionCard>

        <SectionCard icon={Gavel} title="Ν. 5090/2024 — Ποινική Δικονομία" badge="Από 1.5.2024" testId="section-n5090">
          <div className="space-y-3">
            <Item title="Διάρκειες προθεσμιών ένδικων μέσων: αμετάβλητες">
              Έφεση: 10 ημέρες (30 για κάτοικο αλλοδαπής/αγνώστου διαμονής από επίδοση). Αναίρεση: 20 ημέρες. Έφεση κατά βουλεύματος: 10 ημέρες από επίδοση.
            </Item>
            <Item title="Αναστολή Αυγούστου (άρθ. 473§3 ΚΠΔ)">
              Οι προθεσμίες άσκησης ένδικων μέσων <b>αναστέλλονται από 1 έως 31 Αυγούστου</b> — το COURTPLAN την εφαρμόζει αυτόματα σε όλους τους ποινικούς υπολογισμούς.
            </Item>
          </div>
        </SectionCard>

        <SectionCard icon={CheckCircle2} title="Πώς τα χειρίζεται το COURTPLAN" testId="section-courtplan-handling">
          <div className="space-y-3">
            <Item title="Αυτόματη επιλογή καθεστώτος">
              Στην καταχρηστική προθεσμία έφεσης/αναίρεσης το σύστημα επιλέγει μόνο του 1 ή 2 έτη με βάση την ημερομηνία δημοσίευσης που καταχωρείτε.
            </Item>
            <Item title="Ερώτηση όπου χρειάζεται">
              Στην ανακοπή 954 ερωτάστε αν η επιταγή επιδόθηκε πριν ή μετά την 1.1.2026 (15 ή 30 εργάσιμες). Στην κατατεθειμένη έφεση επιλέγετε παλαιό/νέο καθεστώς — στο νέο, οι προθεσμίες υπολογίζονται από την ημερομηνία κατάθεσης.
            </Item>
            <Item title="Νέες προθεσμίες στα ΣτΕ">
              Νέα επιλογή «Κατάθεση δικογράφου ΣτΕ» υπολογίζει αυτόματα το δίμηνο επίδοσης του εισαγωγικού. Οι προθεσμίες δικασίμου ΣτΕ (υπόμνημα, πρόσθετοι λόγοι, παρέμβαση) έχουν ενημερωθεί στον ν. 5119/2024.
            </Item>
          </div>
          <p className="text-xs text-slate-400 border-t border-slate-100 pt-3 mt-2">
            Ο οδηγός έχει ενημερωτικό χαρακτήρα και δεν υποκαθιστά τον έλεγχο της κείμενης νομοθεσίας. Τελευταία ενημέρωση: Ιούνιος 2026.
          </p>
        </SectionCard>
      </main>
    </div>
  );
}
